import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { useTheme, alpha } from '@mui/material/styles'
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import DownloadIcon from '@mui/icons-material/Download'
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'
import { LineChartPro } from '@mui/x-charts-pro'
import { useChartProApiRef } from '@mui/x-charts-pro/hooks'
import { ZOOM_INTERACTION, ZOOM_OPTIONS } from './plot-constants'

/**
 * CustomerPlot — single-series chart with zoom, pan, pause-on-zoom, and PNG
 * export. Adapted from the web framework's MUIPlot for the simpler
 * customer-platform data model (one field per chart card).
 *
 * Zoom is UNCONTROLLED — LineChartPro manages its own zoom state.
 * We observe zoom changes via onZoomChange to:
 *   1. Enable/disable the Reset button
 *   2. Switch between live-scroll mode and paused/zoomed mode
 */
function CustomerPlot ({
	data,
	dataRevision,
	label,
	unit = '',
	color = '#2196f3',
	decimals = 2,
	height = 450,
	liveWindowMs,
	timezone,
}) {
	const theme = useTheme()
	const containerRef = useRef(null)
	const apiRef = useChartProApiRef('line')

	const [isZoomed, setIsZoomed] = useState(false)
	const pointerDownRef = useRef(false)
	const isZoomedRef = useRef(false)

	const dataRef = useRef(data)
	const dataRevisionRef = useRef(dataRevision)
	dataRef.current = data
	dataRevisionRef.current = dataRevision

	const [frozenSnapshot, setFrozenSnapshot] = useState(null)
	const [xZoomRange, setXZoomRange] = useState(null)

	const settingZoomRef = useRef(false)

	const setZoomDataSilent = useCallback((zd) => {
		settingZoomRef.current = true
		apiRef.current?.setZoomData(zd)
		settingZoomRef.current = false
	}, [apiRef])

	const handleChartPointerDown = useCallback(() => {
		pointerDownRef.current = true
	}, [])
	const handleChartPointerUp = useCallback(() => {
		pointerDownRef.current = false
	}, [])

	const handleZoomChange = useCallback((zoomData) => {
		if (settingZoomRef.current) return
		const zoomed = zoomData?.some(z => z.start > 0.5 || z.end < 99.5)

		if (zoomed) {
			if (!isZoomedRef.current) {
				setFrozenSnapshot({
					data: dataRef.current,
					dataRevision: dataRevisionRef.current,
				})
			}
			isZoomedRef.current = true
			setIsZoomed(true)

			const xEntry = zoomData.find(z => z.axisId === 'x')
			if (xEntry) {
				setXZoomRange({ start: xEntry.start, end: xEntry.end })
			}
		} else if (isZoomedRef.current) {
			isZoomedRef.current = false
			setIsZoomed(false)
			setFrozenSnapshot(null)
			setXZoomRange(null)
		}
	}, [])

	const allAxisIds = useMemo(() => ['x', 'y'], [])

	// Live window scroll — advance the x viewport when new data arrives
	const prevRevisionRef = useRef(dataRevision)
	useEffect(() => {
		if (isZoomed) return
		if (prevRevisionRef.current === dataRevision) return
		prevRevisionRef.current = dataRevision

		if (!data?.length || !apiRef.current?.setZoomData) return

		const firstTs = new Date(data[0].timestamp).getTime()
		const lastTs = new Date(data[data.length - 1].timestamp).getTime()
		const totalMs = lastTs - firstTs
		if (totalMs <= 0) return

		if (liveWindowMs != null) {
			const startPct = Math.max(0, ((totalMs - liveWindowMs) / totalMs) * 100)
			setZoomDataSilent([{ axisId: 'x', start: startPct, end: 100 }])
		} else {
			setZoomDataSilent([{ axisId: 'x', start: 0, end: 100 }])
		}
	}, [dataRevision, isZoomed, data, liveWindowMs, setZoomDataSilent, apiRef])

	const handleZoomIn = useCallback(() => {
		apiRef.current?.zoomIn()
		setIsZoomed(true)
	}, [apiRef])
	const handleZoomOut = useCallback(() => {
		apiRef.current?.zoomOut()
		setIsZoomed(true)
	}, [apiRef])
	const handleZoomReset = useCallback(() => {
		setZoomDataSilent(allAxisIds.map(id => ({ axisId: id, start: 0, end: 100 })))
		isZoomedRef.current = false
		setIsZoomed(false)
		setFrozenSnapshot(null)
		setXZoomRange(null)
	}, [setZoomDataSilent, allAxisIds])

	// PNG export
	const handleDownload = useCallback(() => {
		if (!containerRef.current) return
		const svg = containerRef.current.querySelector('svg')
		if (!svg) return

		const svgClone = svg.cloneNode(true)
		const bbox = svg.getBoundingClientRect()
		const w = bbox.width
		const h = bbox.height

		const inlineStyles = (element, clonedElement) => {
			const computedStyle = window.getComputedStyle(element)
			for (let i = 0; i < computedStyle.length; i++) {
				const prop = computedStyle[i]
				clonedElement.style[prop] = computedStyle.getPropertyValue(prop)
			}
			const children = element.children
			const clonedChildren = clonedElement.children
			for (let i = 0; i < children.length; i++) {
				inlineStyles(children[i], clonedChildren[i])
			}
		}

		inlineStyles(svg, svgClone)

		const svgString = new XMLSerializer().serializeToString(svgClone)
		const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
		const url = URL.createObjectURL(svgBlob)

		const img = new Image()
		img.onload = () => {
			const canvas = document.createElement('canvas')
			canvas.width = w * 2
			canvas.height = h * 2
			const ctx = canvas.getContext('2d')
			ctx.fillStyle = theme.palette.mode === 'dark' ? '#121212' : '#ffffff'
			ctx.fillRect(0, 0, canvas.width, canvas.height)
			ctx.scale(2, 2)
			ctx.drawImage(img, 0, 0, w, h)

			canvas.toBlob((blob) => {
				const pngUrl = URL.createObjectURL(blob)
				const a = document.createElement('a')
				a.href = pngUrl
				a.download = `${label || 'chart'}.png`
				a.click()
				URL.revokeObjectURL(pngUrl)
				URL.revokeObjectURL(url)
			}, 'image/png')
		}
		img.src = url
	}, [theme, label])

	// When zoomed, compute tight y-axis bounds for the visible x-range
	const yAxisFit = useMemo(() => {
		if (!xZoomRange || !frozenSnapshot?.data?.length) return null

		const src = frozenSnapshot.data
		const firstTs = new Date(src[0].timestamp).getTime()
		const totalMs = new Date(src[src.length - 1].timestamp).getTime() - firstTs
		if (totalMs <= 0) return null

		const xStartTs = firstTs + totalMs * (xZoomRange.start / 100)
		const xEndTs = firstTs + totalMs * (xZoomRange.end / 100)

		let vMin = Infinity
		let vMax = -Infinity
		for (let i = 0; i < src.length; i++) {
			const ts = new Date(src[i].timestamp).getTime()
			if (ts < xStartTs || ts > xEndTs) continue
			const v = src[i].value
			if (v == null) continue
			if (v < vMin) vMin = v
			if (v > vMax) vMax = v
		}
		if (vMin === Infinity) return null
		const range = vMax - vMin
		const pad = Math.max(range * 0.05, 0.001)
		return { min: vMin - pad, max: vMax + pad }
	}, [xZoomRange, frozenSnapshot])

	// Compute the short timezone abbreviation shown in the x-axis label
	const tzAbbr = timezone
		? (new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'short' })
				.formatToParts(new Date())
				.find(p => p.type === 'timeZoneName')?.value ?? timezone)
		: null

	const effectiveData = frozenSnapshot?.data ?? data

	if (!effectiveData?.length) {
		return (
			<Box sx={{ p: 4, textAlign: 'center' }}>
				<Typography variant="body2" color="text.secondary">
					No data available
				</Typography>
			</Box>
		)
	}

	// Prepare chart data arrays
	const xData = effectiveData.map(p => new Date(p.timestamp))
	const yData = effectiveData.map(p => p.value ?? null)

	const xMin = xData.length >= 2 ? xData[0] : undefined
	const xMax = xData.length >= 2 ? xData[xData.length - 1] : undefined

	if (xData.length < 2) {
		return (
			<Box sx={{ p: 4, textAlign: 'center' }}>
				<Typography variant="body2" color="text.secondary">
					Waiting for data...
				</Typography>
			</Box>
		)
	}

	return (
		<Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
			{/* Toolbar */}
			<Stack
				direction="row"
				alignItems="center"
				spacing={0.5}
				sx={{ px: 0.75, py: 0.25, flexShrink: 0 }}
			>
				<Tooltip title="Zoom in">
					<IconButton size="small" onClick={handleZoomIn} sx={{ p: 0.5 }}>
						<ZoomInIcon sx={{ fontSize: 18 }} />
					</IconButton>
				</Tooltip>
				<Tooltip title="Zoom out">
					<IconButton size="small" onClick={handleZoomOut} sx={{ p: 0.5 }}>
						<ZoomOutIcon sx={{ fontSize: 18 }} />
					</IconButton>
				</Tooltip>
				<Tooltip title="Reset zoom">
					<span>
						<IconButton
							size="small"
							onClick={handleZoomReset}
							disabled={!isZoomed}
							sx={{ p: 0.5 }}
						>
							<ZoomOutMapIcon sx={{ fontSize: 18 }} />
						</IconButton>
					</span>
				</Tooltip>
				{frozenSnapshot && (
					<Tooltip title="Live data paused — reset zoom to resume">
						<Stack direction="row" alignItems="center" spacing={0.25} sx={{ ml: 0.5 }}>
							<PauseCircleOutlineIcon sx={{ fontSize: 14, color: 'warning.main' }} />
							<Typography
								variant="caption"
								sx={{
									color: 'warning.main',
									fontWeight: 600,
									fontSize: '0.65rem',
									lineHeight: 1,
								}}
							>
								PAUSED
							</Typography>
						</Stack>
					</Tooltip>
				)}
				<Box sx={{ flex: 1 }} />
				<Tooltip title="Download PNG">
					<IconButton size="small" onClick={handleDownload} sx={{ p: 0.5 }}>
						<DownloadIcon sx={{ fontSize: 18 }} />
					</IconButton>
				</Tooltip>
			</Stack>

			{/* Chart area */}
			<Box
				ref={containerRef}
				onPointerDown={handleChartPointerDown}
				onPointerUp={handleChartPointerUp}
				sx={{
					width: '100%',
					border: `1px solid ${theme.palette.divider}`,
					borderRadius: 1,
					overflow: 'hidden',
					bgcolor: 'background.paper',
				}}
			>
				<LineChartPro
					apiRef={apiRef}
					series={[{
						type: 'line',
						id: 'value',
						label,
						yAxisId: 'y',
						data: yData,
						color,
						showMark: false,
						curve: 'linear',
						valueFormatter: (v) =>
							v == null ? '—' : `${v.toFixed(decimals)} ${unit}`,
					}]}
					skipAnimation
					xAxis={[{
						id: 'x',
						data: xData,
						scaleType: 'time',
						min: xMin,
						max: xMax,
						label: tzAbbr ? `Time (${tzAbbr})` : undefined,
						valueFormatter: timezone
							? (v, context) => {
								const date = new Date(v)
								if (context?.location === 'tooltip') {
									return date.toLocaleString('en-US', {
										timeZone: timezone,
										year: 'numeric',
										month: 'short',
										day: 'numeric',
										hour: '2-digit',
										minute: '2-digit',
										second: '2-digit',
										hour12: false,
										timeZoneName: 'short',
									})
								}
								return date.toLocaleString('en-US', {
									timeZone: timezone,
									month: 'short',
									day: 'numeric',
									hour: '2-digit',
									minute: '2-digit',
									hour12: false,
								})
							}
							: (v) => new Date(v).toLocaleTimeString('en-US', {
								hour: '2-digit',
								minute: '2-digit',
								second: '2-digit',
								hour12: false,
							}),
						zoom: ZOOM_OPTIONS,
					}]}
					yAxis={[{
						id: 'y',
						min: yAxisFit?.min,
						max: yAxisFit?.max,
						label: unit || label,
						labelStyle: { fill: color, fontWeight: 700, fontSize: 11 },
						tickLabelStyle: { fill: color, fontSize: 10, fontWeight: 700 },
						valueFormatter: (v) => v?.toFixed(decimals) ?? '',
						zoom: ZOOM_OPTIONS,
					}]}
					onZoomChange={handleZoomChange}
					zoomInteractionConfig={ZOOM_INTERACTION}
					height={height}
					margin={{ top: 8, right: 16, bottom: tzAbbr ? 52 : 32, left: 64 }}
					grid={{ horizontal: true, vertical: true }}
					tooltip={{ trigger: 'axis' }}
					axisHighlight={{ x: 'line', y: 'none' }}
					sx={{
						'& .MuiLineElement-root': { strokeWidth: 2 },
						'& .MuiChartsGrid-line': {
							stroke: alpha(theme.palette.divider, 0.45),
						},
						'& .MuiChartsAxis-tickLabel': {
							fill: theme.palette.text.primary,
						},
					}}
				/>
			</Box>
		</Box>
	)
}

export default CustomerPlot
